import { Alert, Button, Group, Stack, Text } from "@mantine/core"
import { Navigate } from "@tanstack/react-router"
import {
  ProblemDetails,
  ValidationProblemDetails,
} from "../../@client/Microsoft_AspNetCore_Mvc"
import { Dictionary } from "../../@client/System_Collections_Generic"
import { useTranslation } from "react-i18next"
import { ErrorContext } from "./ErrorContext"
import { useEffect } from "react"
import {
  AutoReloadProps,
  getReloadAttempt,
  setReloadAttempt,
} from "./auto-reload"

interface LoginErrorDetails extends ProblemDetails {
  LockSeconds?: number | undefined
}

export class DefaultError extends Error {
  status?: number | null
  validationErrors?: Dictionary<string, string[]> | undefined
  LockSeconds?: number | undefined
  response?: Response

  constructor(
    details: Partial<
      ProblemDetails | ValidationProblemDetails | LoginErrorDetails
    >
  ) {
    super(details.detail)
    this.status = details.status
    this.name = details.title ?? "Unknown Error"
    if ((details as LoginErrorDetails)?.LockSeconds) {
      this.LockSeconds = (details as LoginErrorDetails).LockSeconds
    }
    if ((details as ValidationProblemDetails)?.errors) {
      this.validationErrors = (details as ValidationProblemDetails)
        .errors as unknown as Dictionary<string, string[]>
    }

    const isLocalHost =
      typeof location !== "undefined" &&
      (location.hostname === "localhost" || location.hostname === "127.0.0.1")

    if (isLocalHost && !details.title) {
      console.error(details)
    }
  }
}

interface Props extends React.PropsWithChildren {
  autoReload?: AutoReloadProps
}

export function ErrorContextProvider({ children, ...props }: Props) {
  const [error, throwError] = React.useState<DefaultError | null>(null)

  if (error) {
    console.error("Context", error)
  }

  const { t } = useTranslation("app_server")

  // if the user tries to login with invalid credentials,
  // the api return 401 Unauthorized, the naviagtion from login to login causes errors:
  // 	MantineProvider was not found,
  // 	cannot destructure property navigate from useRouter() as it is null
  const errorIsUnauthorizedOnLoginPage =
    error && error.status === 401 && location.href === "/authenticate/login"

  const autoReload = props.autoReload?.enabled === true
  useEffect(() => {
    if (!error || errorIsUnauthorizedOnLoginPage || error.status === 401) {
      return
    }
    const maxRetries = props.autoReload?.retries || 3
    console.error("Caught error in context:", error)
    if (autoReload) {
      let attempts = getReloadAttempt() || 0
      if (attempts < maxRetries) {
        attempts++
        setReloadAttempt(attempts)
        console.log(
          `Auto-reloading page, attempt ${attempts} of ${props.autoReload?.retries}`
        )
        location.reload()
      }
    }
  }, [error, errorIsUnauthorizedOnLoginPage])

  return (
    <ErrorContext.Provider value={{ throwError }}>
      {error && !errorIsUnauthorizedOnLoginPage ? (
        error.status === 401 ? (
          <Navigate
            to="/authenticate/login"
            search={{ returnUrl: location.pathname + location.search }}
          />
        ) : (
          <Alert
            title={error?.name}
            color="red"
            w="100%"
            h="fit-content"
            variant="filled"
          >
            <Stack gap={1}>
              <Text>{error?.message}</Text>
              <Text>{t("ContactYourAdminIfErrorPersists")}</Text>

              {props?.autoReload?.showReloadButton ? (
                <Group>
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => location.reload()}
                  >
                    {t("ReloadPage")}
                  </Button>
                </Group>
              ) : null}
            </Stack>
          </Alert>
        )
      ) : (
        children
      )}
    </ErrorContext.Provider>
  )
}
